package com.banyan.flightviewer.util

import kotlin.math.*

object GeoUtils {

    private const val EARTH_RADIUS_KM = 6371.0

    // Multiplatform replacements for java.lang.Math.toRadians / toDegrees.
    private fun degToRad(d: Double): Double = d * PI / 180.0
    private fun radToDeg(r: Double): Double = r * 180.0 / PI

    /**
     * Calculate the Haversine distance between two geographic points.
     * @return distance in kilometers
     */
    fun haversineDistance(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val dLat = degToRad(lat2 - lat1)
        val dLon = degToRad(lon2 - lon1)
        val rLat1 = degToRad(lat1)
        val rLat2 = degToRad(lat2)

        val a = sin(dLat / 2).pow(2) + cos(rLat1) * cos(rLat2) * sin(dLon / 2).pow(2)
        val c = 2 * asin(sqrt(a))

        return EARTH_RADIUS_KM * c
    }

    /**
     * Calculate a bounding box around a center point with a given radius in km.
     * @return BoundingBox with north, south, east, west bounds
     */
    fun boundingBox(lat: Double, lon: Double, radiusKm: Double): BoundingBox {
        val latDelta = radToDeg(radiusKm / EARTH_RADIUS_KM)
        val lonDelta = radToDeg(radiusKm / (EARTH_RADIUS_KM * cos(degToRad(lat))))

        return BoundingBox(
            north = lat + latDelta,
            south = lat - latDelta,
            east = lon + lonDelta,
            west = lon - lonDelta,
        )
    }

    /** Convert kilometers to nautical miles */
    fun kmToNauticalMiles(km: Double): Double = km * 0.539957

    /** Convert kilometers to statute miles */
    fun kmToMiles(km: Double): Double = km * 0.621371

    /** Convert nautical miles to kilometers */
    fun nauticalMilesToKm(nm: Double): Double = nm / 0.539957

    /** Convert statute miles to kilometers */
    fun milesToKm(miles: Double): Double = miles / 0.621371

    /**
     * Calculate bearing from point 1 to point 2.
     * @return bearing in degrees (0-360)
     */
    fun bearing(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val rLat1 = degToRad(lat1)
        val rLat2 = degToRad(lat2)
        val dLon = degToRad(lon2 - lon1)

        val x = sin(dLon) * cos(rLat2)
        val y = cos(rLat1) * sin(rLat2) - sin(rLat1) * cos(rLat2) * cos(dLon)

        val bearing = radToDeg(atan2(x, y))
        return (bearing + 360) % 360
    }
}

data class BoundingBox(
    val north: Double,
    val south: Double,
    val east: Double,
    val west: Double,
)
